import type { PolicyEnvelope, PolicyMeta } from './types'

export const POLICY_ERROR_CODE = {
  CONFLICT: 'OUTBOUND_POLICY_CONFLICT',
  FEATURE_DISABLED: 'SCHEDULE_FEATURE_DISABLED',
} as const

export const POLICY_ERROR_MESSAGE = {
  CONFLICT: '다른 운영과 기간이 겹치거나 다른 사용자가 먼저 변경했습니다. 새로고침 후 다시 확인하세요.',
  FEATURE_DISABLED: '출고 정책 기능이 아직 활성화되지 않았습니다.',
  GENERIC: '출고 정책 요청에 실패했습니다. 잠시 후 다시 시도하세요.',
} as const

/** 실 API 실패. wiring 은 message 만 표시한다. code/status 는 진단용. */
export class AllocationPriorityApiError extends Error {
  readonly code?: string

  readonly status?: number

  constructor(message: string, options: { code?: string; status?: number } = {}) {
    super(message)
    this.name = 'AllocationPriorityApiError'
    this.code = options.code
    this.status = options.status
  }
}

const isObject = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null && !Array.isArray(value)

const metaOf = (body: unknown): PolicyMeta | undefined => (isObject(body) && isObject(body.meta) ? (body.meta as PolicyMeta) : undefined)

/** errorCode 기준 분기. 메시지 문구로 분기하지 않는다. 5xx 와 meta 없는 응답은 일반 실패 문구. */
const fromMeta = (meta: PolicyMeta | undefined, status?: number): AllocationPriorityApiError => {
  const code = meta?.errorCode
  if (code === POLICY_ERROR_CODE.CONFLICT) return new AllocationPriorityApiError(POLICY_ERROR_MESSAGE.CONFLICT, { code, status })
  if (code === POLICY_ERROR_CODE.FEATURE_DISABLED) return new AllocationPriorityApiError(POLICY_ERROR_MESSAGE.FEATURE_DISABLED, { code, status })
  const serverError = status !== undefined && status >= 500
  const message = typeof meta?.message === 'string' && meta.message.trim() ? meta.message : undefined
  if (message && !serverError) return new AllocationPriorityApiError(message, { code, status })
  return new AllocationPriorityApiError(POLICY_ERROR_MESSAGE.GENERIC, { code, status })
}

/** request 계층이 던진 값(axios 오류, `{error:{message:'empty'}}`, 네트워크 오류)을 화면용 Error 로 바꾼다. */
export const toPolicyError = (reason: unknown): AllocationPriorityApiError => {
  if (reason instanceof AllocationPriorityApiError) return reason
  const response = isObject(reason) && isObject(reason.response) ? reason.response : undefined
  const status = typeof response?.status === 'number' ? response.status : undefined
  return fromMeta(metaOf(response?.data), status)
}

/** 성공 HTTP 응답의 envelope 를 검증한다. 비 JSON(문자열/HTML) 또는 meta.result≠SUCCESS 는 실패다. */
export const unwrapEnvelope = <T>(body: unknown): T | undefined => {
  const meta = metaOf(body)
  if (!meta) throw new AllocationPriorityApiError(POLICY_ERROR_MESSAGE.GENERIC)
  if (meta.result !== 'SUCCESS') throw fromMeta(meta)
  return (body as PolicyEnvelope<T>).data
}
